import type { AgricobotCompanionAsset } from '@/modules/agricobots/assets/AgricobotCompanion';
import { supabase } from '@/service/supabaseClient';
import { useAuthStore } from '@/stores/auth';
import { AgricobotAsset } from '@/types/AgricobotAsset';
import { AssetTypes } from '@/types/AssetTypes';
import type { GenericAsset, InboundRecord } from '@/types/GenericAsset';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef, triggerRef, watch } from 'vue';

type AssetStoreRealtimeChannel = ReturnType<typeof supabase.channel>;
const DEFAULT_ASSET_INBOUND_LATEST_LIMIT = 15;

type AssetInboundRpcRow = {
  node_id: string | null;
  asset_id: string | null;
  device_id: string | null;
  device_name: string | null;
  message_type: string | null;
  payload: Record<string, unknown> | null;
  received_at: string | null;
  schema_version: string | null;
  content: string | null;
  content_type: string | null;
};

function getAssetInboundLatestLimit(): number {
  const configuredLimit = Number(import.meta.env.VITE_ASSET_INBOUND_LATEST_LIMIT);

  if (Number.isInteger(configuredLimit) && configuredLimit > 0) {
    return configuredLimit;
  }

  return DEFAULT_ASSET_INBOUND_LATEST_LIMIT;
}

/**
 * Centralized asset store for managing all assets across the application.
 * Provides single source of truth with realtime updates and admin impersonation support.
 */
export const useAssetStore = defineStore('assets', () => {
  // ============================================================================
  // State
  // ============================================================================

  /**
   * Map of all assets keyed by unique identifier (node_id_asset_id_device_id)
   */
  const assets = shallowRef<Map<string, GenericAsset>>(new Map());

  /**
   * Loading state for async operations
   */
  const loading = ref(false);

  /**
   * True until the first successful fetch completes.
   * Reset to true on clear() (enterprise change) so the spinner
   * shows again for the new enterprise's first load.
   */
  const isInitialLoad = ref(true);

  /** Monotonic counter to discard stale fetchAssets() responses */
  let fetchVersion = 0;

  /**
   * Error message if any operation fails
   */
  const error = ref<string | null>(null);

  /**
   * Active realtime subscription channel
   */
  const realtimeChannel = ref<AssetStoreRealtimeChannel | null>(null);

  // ============================================================================
  // Realtime Update Throttle
  // ============================================================================

  /**
   * Throttle timer for batching realtime updates.
   * shallowRef means Vue doesn't track deep mutations — we manually
   * call triggerRef(assets) at most once every THROTTLE_MS to batch
   * multiple telemetry events into a single UI update.
   */
  const REALTIME_UPDATE_THROTTLE_MS = 300;
  let realtimeUpdateTimer: ReturnType<typeof setTimeout> | null = null;
  let hasPendingUpdates = false;

  function scheduleReactivityFlush(): void {
    hasPendingUpdates = true;
    if (realtimeUpdateTimer !== null) return;
    realtimeUpdateTimer = setTimeout(() => {
      realtimeUpdateTimer = null;
      if (hasPendingUpdates) {
        hasPendingUpdates = false;
        triggerRef(assets);
      }
    }, REALTIME_UPDATE_THROTTLE_MS);
  }

  // ============================================================================
  // Helper Functions
  // ============================================================================

  /**
   * Builds unique asset key from identifiers
   */
  function buildAssetKey(nodeId: string, assetId: string, deviceId: string): string {
    return `${nodeId}_${assetId}_${deviceId}`;
  }

  /**
   * Determines asset type from inbound record
   */
  function determineAssetType(record: InboundRecord): AssetTypes {
    // Logic to determine asset type from record
    // For now, check if it's a companion based on message_type or other fields
    // This is a simplified version - adjust based on your actual logic
    if (record.message_type?.includes('companion')) {
      return AssetTypes.AGRICOBOT_COMPANION;
    }
    return AssetTypes.AGRICOBOT;
  }

  /**
   * Creates asset instance from inbound record using factory pattern
   * Type-agnostic design allows easy addition of future asset types
   */
  function createAssetFromInbound(record: InboundRecord): GenericAsset {
    const assetType = determineAssetType(record);

    switch (assetType) {
      case AssetTypes.AGRICOBOT:
        return new AgricobotAsset([record]);
      case AssetTypes.AGRICOBOT_COMPANION:
        // Note: Companions are typically created through their parent asset
        // This case handles direct creation if needed
        return new AgricobotAsset([record]); // Adjust if you have a separate companion constructor
      // Future asset types can be added here:
      // case AssetTypes.NEW_ASSET_TYPE:
      //   return new NewAssetType([record]);
      default:
        console.error(`Unknown asset type: ${assetType}, defaulting to AgricobotAsset`);
        return new AgricobotAsset([record]);
    }
  }

  // ============================================================================
  // Getters
  // ============================================================================

  /**
   * All assets as an array
   */
  const allAssets = computed(() => Array.from(assets.value.values()));

  /**
   * Assets flattened with companions, sorted with online assets first
   * Maintains companion grouping under their master assets
   */
  const assetsWithCompanions = computed((): GenericAsset[] => {
    const fetched = allAssets.value.filter(
      (a) => a.getAssetType() === AssetTypes.AGRICOBOT,
    ) as unknown as AgricobotAsset[];

    // Build flat list including companions
    const all: GenericAsset[] = [];
    for (const a of fetched) {
      all.push(a as unknown as GenericAsset);
      if (a.getAssetType() === AssetTypes.AGRICOBOT) {
        const companions = a.getCompanions();
        all.push(...(companions as unknown as GenericAsset[]));
      }
    }

    // Keep companions grouped under masters and sort online-first
    const buildId = (x: {
      node_id: string;
      asset_id: string;
      device_id: string;
    }) => `${x.node_id}_${x.asset_id}_${x.device_id}`;

    const companions = all.filter((x) => x.getAssetType() === AssetTypes.AGRICOBOT_COMPANION);
    const nonCompanions = all.filter((x) => x.getAssetType() !== AssetTypes.AGRICOBOT_COMPANION);

    const sortedNonCompanions = nonCompanions.sort((a, b) =>
      a.asset_id.localeCompare(b.asset_id),
    );

    const masterIndex = new Map<string, number>();
    for (let i = 0; i < sortedNonCompanions.length; i++) {
      masterIndex.set(buildId(sortedNonCompanions[i]), i);
    }

    const placedAfter = new Map<string, number>();
    for (const c of companions) {
      const comp = c as unknown as AgricobotCompanionAsset;
      const masterId = buildId(comp.masterAsset);
      const idx = masterIndex.get(masterId);
      if (idx !== undefined) {
        const offset = placedAfter.get(masterId) ?? 0;
        const insertAt = idx + 1 + offset;
        sortedNonCompanions.splice(insertAt, 0, c);
        placedAfter.set(masterId, offset + 1);
        for (const [id, pos] of masterIndex.entries()) {
          if (pos >= insertAt) masterIndex.set(id, pos + 1);
        }
      } else {
        // Orphan companion
        sortedNonCompanions.push(c);
      }
    }

    return sortedNonCompanions as GenericAsset[];
  });

  /**
   * Get asset by identifiers
   */
  function getAssetById(
    nodeId: string,
    assetId: string,
    deviceId: string,
  ): GenericAsset | undefined {
    return assets.value.get(buildAssetKey(nodeId, assetId, deviceId)) as GenericAsset | undefined;
  }

  /**
   * Online assets only
   */
  const onlineAssets = computed(() => allAssets.value.filter((asset) => asset.isOnline()));

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Fetches all assets for the specified enterprise identifier
   * Uses explicit WHERE clause instead of relying solely on RLS
   * @param enterpriseId - Optional enterprise identifier, defaults to effective company
   */
  async function fetchAssets(enterpriseId?: string): Promise<void> {
    if (isInitialLoad.value) {
      loading.value = true;
    }
    error.value = null;
    const currentVersion = ++fetchVersion;

    try {
      const authStore = useAuthStore();
      const effectiveId =
        enterpriseId ?? (await authStore.getEffectiveCompanyEnterpriseIdentifier?.());

      if (!effectiveId) {
        throw new Error('No enterprise identifier available');
      }

      // Il registro parte INSIEME alla lettura degli asset, non dopo.
      //
      // Prima arrivava in coda, e per il tempo di quella seconda richiesta le
      // macchine si mostravano con la matricola per poi cambiare nome: una
      // ricarica dell'applicazione faceva sfarfallare i nomi fra i due valori.
      // Partendo insieme non costa niente - le due richieste viaggiano
      // affiancate - e il primo disegno ha gia' il nome giusto.
      const registryReady = loadRegistryInfo();

      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_enterprise_device_inbound_latest',
        {
          p_enterprise: effectiveId,
          p_limit_per_device: getAssetInboundLatestLimit(),
        },
      );

      // Discard stale response if a newer fetch was initiated
      if (currentVersion !== fetchVersion) {
        console.log('[AssetStore] Discarding stale fetch response');
        return;
      }

      if (rpcError) {
        throw new Error(`Failed to fetch enterprise inbound snapshot: ${rpcError.message}`);
      }

      if (!rpcData || rpcData.length === 0) {
        console.log('[AssetStore] No assets found for this enterprise');
        assets.value = new Map();
        return;
      }

      const statusesByKey = new Map<string, InboundRecord[]>();

      for (const status of rpcData as AssetInboundRpcRow[]) {
        const nodeId = status.node_id ?? '';
        const assetId = status.asset_id ?? '';
        const deviceId = status.device_id ?? '';

        if (!nodeId || !assetId || !deviceId) {
          continue;
        }

        const key = buildAssetKey(nodeId, assetId, deviceId);
        const inboundRecord: InboundRecord = {
          asset_id: assetId,
          node_id: nodeId,
          device_id: deviceId,
          device_name: status.device_name,
          payload: status.payload ?? {},
          message_type: status.message_type,
          received_at: status.received_at ?? '',
          schema_version: status.schema_version,
          content: status.content,
          content_type: status.content_type,
        };

        const currentStatuses = statusesByKey.get(key);
        if (currentStatuses) {
          currentStatuses.push(inboundRecord);
        } else {
          statusesByKey.set(key, [inboundRecord]);
        }
      }

      const newAssets = new Map<string, GenericAsset>();

      // Aspetta il registro solo ora: e' partito prima della lettura degli
      // asset, quindi a questo punto e' quasi sempre gia' arrivato.
      await registryReady;

      for (const [key, inboundRecords] of statusesByKey.entries()) {
        if (inboundRecords.length === 0) {
          continue;
        }

        const asset = new AgricobotAsset(inboundRecords);
        applyRegistryInfo(key, asset as unknown as GenericAsset);
        newAssets.set(key, asset as unknown as GenericAsset);
      }

      assets.value = newAssets;
      console.log(`[AssetStore] Loaded ${assets.value.size} assets`);
    } catch (err) {
      if (currentVersion !== fetchVersion) return;
      const message = err instanceof Error ? err.message : 'Unknown error';
      error.value = message;
      console.error('[AssetStore] Error fetching assets:', err);
      throw err;
    } finally {
      if (currentVersion === fetchVersion) {
        loading.value = false;
        isInitialLoad.value = false;
      }
    }
  }

  /**
   * Subscribes to realtime updates for the inbound table
   * Filters by effective enterprise identifier
   */
  /**
   * Subscribes to realtime updates for:
   * 1. 'inbound' table (telemetry) - filtered in memory
   * 2. 'device_registry' table (asset management) - filtered by enterprise
   */
  function subscribeToRealtime(): void {
    // Unsubscribe from existing channel if any
    if (realtimeChannel.value) {
      console.log('[AssetStore] Unsubscribing from existing realtime channel');
      void supabase.removeChannel(
        realtimeChannel.value as unknown as Parameters<typeof supabase.removeChannel>[0],
      );
      realtimeChannel.value = null;
    }

    const startAuthStore = useAuthStore();
    const effectiveId = startAuthStore.getEffectiveCompanyEnterpriseIdentifier?.();

    if (!effectiveId) {
      console.error('[AssetStore] Cannot subscribe to realtime: no enterprise identifier');
      return;
    }

    console.log(`[AssetStore] Subscribing to realtime updates for enterprise: ${effectiveId}`);

    // Subscribe to changes
    realtimeChannel.value = supabase
      .channel('asset-store-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'inbound',
          // Note: Supabase realtime filters don't support filtering by enterprise_identifier directly
          // We'll filter in the callback
        },
        (payload) => {
          handleInboundUpdate(payload);
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'device_registry',
          filter: `enterprise=eq.${effectiveId}`,
        },
        (payload) => {
          handleDeviceRegistryUpdate(payload);
        },
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[AssetStore] Subscribed to realtime updates');
        } else if (status === 'CLOSED') {
          console.log('[AssetStore] Realtime subscription closed');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[AssetStore] Realtime subscription error');
        }
      });
  }

  /**
   * Handles realtime update events for 'inbound' table
   * STRICT FILTER: Only updates assets that already exist in the store.
   */
  function handleInboundUpdate(payload: unknown): void {
    const event = payload as {
      eventType?: 'INSERT' | 'UPDATE' | 'DELETE';
      new?: Record<string, unknown>;
      old?: Record<string, unknown>;
    };

    const eventType = event.eventType;
    // Verbose logging?
    // console.log(`[AssetStore] Inbound event: ${eventType}`);

    if (eventType === 'INSERT' || eventType === 'UPDATE') {
      const record = event.new as unknown as InboundRecord;

      // STRICT FILTER: Check if asset exists in store
      const key = buildAssetKey(record.node_id, record.asset_id, record.device_id);
      if (!assets.value.has(key)) {
        // Ignore updates for assets we don't know about (prevents data leak from other companies)
        return;
      }

      updateAsset(record);
    }
    // We intentionally ignore DELETE on inbound - historical data deletion shouldn't remove the asset from the map
  }

  /**
   * La versione dell'injector dentro `device_registry.properties`, quando c'e.
   *
   * Tollerante di proposito: `properties` e una colonna JSONB libera, ci puo
   * finire dentro qualunque cosa, e una macchina con un injector piu vecchio
   * non ha quella chiave. Nessuno di questi casi e un errore - sono solo
   * "versione sconosciuta".
   */
  function readInjectorVersion(properties: unknown): string | null {
    if (!properties || typeof properties !== 'object') return null;
    const value = (properties as Record<string, unknown>).injector_version;
    return typeof value === 'string' && value.trim() !== '' ? value : null;
  }

  /**
   * Cio' che il registro sa di ogni macchina: l'alias e la versione
   * dell'injector, per chiave.
   *
   * Esiste perche' gli oggetti asset vengono ricostruiti da zero a ogni
   * caricamento - e ne arrivano di nuovi dal realtime - e un oggetto appena
   * costruito non sa niente dell'alias. Senza questa memoria, ogni ricarica
   * riportava sullo schermo la matricola per il tempo di una richiesta, poi di
   * nuovo il nome: da fuori, un nome che sfarfalla fra due valori a ogni
   * apertura dell'applicazione.
   *
   * Sta fuori dallo stato reattivo di proposito: non si disegna, serve solo a
   * rivestire gli oggetti nel momento in cui nascono.
   */
  const registryInfo = new Map<
    string,
    { alias: string | null; injectorVersion: string | null; model: string | null }
  >();

  /**
   * Reads what the registry knows about each machine - l'alias e la versione
   * dell'injector - into the cache above.
   *
   * A separate query rather than an extra column on the RPC, so this feature
   * needs no change to a function the whole dashboard depends on. The SELECT
   * policy on device_registry already limits the rows to the caller's own
   * company, so there is nothing to filter here.
   *
   * Every failure is swallowed on purpose: these are labels. If a column does
   * not exist yet, or the request fails, the machines keep the names they have
   * always had and nobody sees an error about something cosmetic.
   */
  async function loadRegistryInfo(): Promise<void> {
    const { data, error: aliasError } = await supabase
      .from('device_registry')
      .select('node_id, asset_id, device_id, alias, device_model, properties');

    if (aliasError || !data) {
      console.debug('[AssetStore] Aliases unavailable, showing serials', aliasError?.message);
      return;
    }

    for (const row of data) {
      if (!row.node_id || !row.asset_id || !row.device_id) continue;
      registryInfo.set(buildAssetKey(row.node_id, row.asset_id, row.device_id), {
        alias: row.alias ?? null,
        injectorVersion: readInjectorVersion(row.properties),
        // Stringa vuota e colonna assente sono la stessa cosa: nessuno l'ha
        // configurato, e si mostra quello che dichiara la macchina.
        model: typeof row.device_model === 'string' && row.device_model.trim() !== ''
          ? row.device_model.trim()
          : null,
      });
    }
  }

  /** Riveste un asset appena costruito con cio' che il registro gia' sapeva. */
  function applyRegistryInfo(key: string, asset: GenericAsset): void {
    const info = registryInfo.get(key);
    if (!info) return;
    asset.alias = info.alias;
    asset.injectorVersion = info.injectorVersion;
    asset.model = info.model;
  }

  /**
   * Rilegge il registro e riveste le macchine gia' in scena.
   *
   * Resta per i casi in cui il registro cambia senza che gli asset vengano
   * ricostruiti - una macchina rinominata da un'altra scheda, per esempio.
   */
  async function applyAliases(): Promise<void> {
    await loadRegistryInfo();

    let applied = 0;
    for (const [key, asset] of assets.value.entries()) {
      if (!registryInfo.has(key)) continue;
      applyRegistryInfo(key, asset);
      applied += 1;
    }

    if (applied > 0) {
      // The Map is what Vue tracks, not the instances inside it
      assets.value = new Map(assets.value);
    }
  }

  /**
   * Applies a rename to the asset already in the store.
   *
   * Exists because writing the field on the instance is not enough: Vue tracks
   * the Map, not the objects inside it, so a rename done in one panel left
   * every other view - the cards on the map above all - showing the old name
   * until a reload. Replacing the Map reference is what makes them redraw.
   */
  function setAlias(nodeId: string, assetId: string, deviceId: string, alias: string | null): void {
    const key = buildAssetKey(nodeId, assetId, deviceId);
    const asset = assets.value.get(key);
    if (!asset) return;

    asset.alias = alias;
    // Anche nella memoria, o la prossima ricostruzione rimetterebbe il vecchio
    const info = registryInfo.get(key);
    registryInfo.set(key, {
      alias,
      injectorVersion: info?.injectorVersion ?? null,
      model: info?.model ?? null,
    });
    assets.value = new Map(assets.value);
  }

  /**
   * Handles realtime update events for 'device_registry' table
   * Adds or removes assets from the store based on registry changes.
   */
  function handleDeviceRegistryUpdate(payload: unknown): void {
    const event = payload as {
      eventType?: 'INSERT' | 'UPDATE' | 'DELETE';
      new?: Record<string, unknown>;
      old?: Record<string, unknown>;
    };

    const eventType = event.eventType;

    if (eventType === 'INSERT') {
      // New asset registered for this company
      const newDevice = event.new as {
        node_id: string;
        asset_id: string;
        device_id: string;
        device_name: string;
      };
      console.log(`[AssetStore] New device registered: ${newDevice.device_name}`);

      // We need to fetch the latest state for this new asset
      // Using a dummy record initially or fetching real data?
      // Best approach: Try to fetch latest inbound record for this asset
      void fetchLatestStateForNewAsset(newDevice);
    } else if (eventType === 'UPDATE') {
      /**
       * Almost always a rename: the machine's own upsert writes the same values
       * over themselves at every heartbeat, so most UPDATEs change nothing.
       *
       * Applied in place rather than by refetching, because a rename must reach
       * the other people looking at the same fleet without a full reload - and
       * refetching on every heartbeat-driven UPDATE would be one round trip per
       * machine per minute for nothing.
       */
      const updated = event.new as {
        node_id?: string;
        asset_id?: string;
        device_id?: string;
        alias?: string | null;
      };
      const previous = event.old as { alias?: string | null } | undefined;

      if (!updated?.node_id || !updated.asset_id || !updated.device_id) return;
      // `old` carries only the replica identity unless REPLICA IDENTITY FULL is
      // set, so a missing previous value is normal: apply it and move on.
      if (previous && previous.alias === updated.alias) return;

      const key = buildAssetKey(updated.node_id, updated.asset_id, updated.device_id);
      const asset = assets.value.get(key);
      if (asset) {
        asset.alias = updated.alias ?? null;
        const info = registryInfo.get(key);
        registryInfo.set(key, {
          alias: updated.alias ?? null,
          injectorVersion: info?.injectorVersion ?? null,
          model: info?.model ?? null,
        });
        // The Map itself is what Vue tracks, not the instances inside it
        assets.value = new Map(assets.value);
      }
    } else if (eventType === 'DELETE') {
      // Asset removed from company
      const oldDevice = event.old as {
        node_id: string;
        asset_id: string;
        device_id: string;
      };
      if (oldDevice) {
        removeAsset(oldDevice.node_id, oldDevice.asset_id, oldDevice.device_id);
      }
    }
  }

  /**
   * Helper to fetch state for a newly registered asset
   */
  async function fetchLatestStateForNewAsset(device: {
    node_id: string;
    asset_id: string;
    device_id: string;
  }) {
    const { data, error } = await supabase
      .from('inbound')
      .select(
        'node_id, asset_id, device_id, device_name, message_type, payload, received_at, schema_version',
      )
      .eq('node_id', device.node_id)
      .eq('asset_id', device.asset_id)
      .eq('device_id', device.device_id)
      .order('received_at', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) {
      // No data yet, maybe just create a placeholder asset?
      // For now, if no inbound data, we can't create an AgricobotAsset easily without mocking data.
      // Let's create a minimal record if possible, or wait for first inbound.
      console.log(
        `[AssetStore] No inbound data yet for new device ${device.asset_id}, waiting for first heartbeat.`,
      );
      return;
    }

    const record: InboundRecord = {
      asset_id: data.asset_id || '',
      node_id: data.node_id || '',
      device_id: data.device_id || '',
      device_name: data.device_name || '',
      payload: data.payload as Record<string, unknown>,
      message_type: data.message_type || '',
      received_at: data.received_at || '',
      schema_version: data.schema_version || '',
      content: null,
      content_type: null,
    };

    updateAsset(record);
  }

  /**
   * Updates an existing asset or creates a new one from inbound record
   */
  function updateAsset(inboundRecord: InboundRecord): void {
    const key = buildAssetKey(
      inboundRecord.node_id,
      inboundRecord.asset_id,
      inboundRecord.device_id,
    );
    const existing = assets.value.get(key);

    if (existing) {
      existing.updateInboundRecord([inboundRecord]);
    } else {
      const newAsset = createAssetFromInbound(inboundRecord);
      // Anche una macchina che compare a stream aperto nasce con il suo nome,
      // invece di mostrare la matricola fino alla ricarica successiva.
      applyRegistryInfo(key, newAsset);
      assets.value.set(key, newAsset);
      console.log(`[AssetStore] Created new asset: ${key}`);
    }

    // Batch UI updates: with shallowRef, deep mutations are invisible
    // to Vue until we manually trigger. This batches 1-5 events/sec
    // into ~3 UI updates/sec max.
    scheduleReactivityFlush();
  }

  /**
   * Removes an asset from the store
   */
  function removeAsset(nodeId: string, assetId: string, deviceId: string): void {
    const key = buildAssetKey(nodeId, assetId, deviceId);
    if (assets.value.delete(key)) {
      console.log(`[AssetStore] Removed asset: ${key}`);
      scheduleReactivityFlush();
    }
  }

  /**
   * Unsubscribes from realtime updates
   */
  function unsubscribeFromRealtime(): void {
    if (realtimeChannel.value) {
      console.log('[AssetStore] Unsubscribing from realtime');
      void supabase.removeChannel(
        realtimeChannel.value as unknown as Parameters<typeof supabase.removeChannel>[0],
      );
      realtimeChannel.value = null;
    }
  }

  /**
   * Force refetch assets from database
   * Useful for catching missed realtime events (especially DELETE operations)
   */
  async function refreshAssets(): Promise<void> {
    console.log('[AssetStore] Manually refreshing assets');
    await fetchAssets();
  }

  /**
   * Clears all assets from the store
   */
  function clear(): void {
    if (realtimeUpdateTimer !== null) {
      clearTimeout(realtimeUpdateTimer);
      realtimeUpdateTimer = null;
      hasPendingUpdates = false;
    }
    isInitialLoad.value = true;
    assets.value = new Map();
    error.value = null;
    console.log('[AssetStore] Cleared all assets');
  }

  // ============================================================================
  // Watchers
  // ============================================================================

  /**
   * Watch for enterprise identifier changes (handles login, logout, impersonation, and account switching)
   * This ensures assets are properly initialized and cleared for all authentication state changes
   */
  const authStore = useAuthStore();
  watch(
    () => authStore.getEffectiveCompanyEnterpriseIdentifier?.(),
    async (newIdentifier, oldIdentifier) => {
      console.log(
        `[AssetStore] Enterprise identifier changed: "${oldIdentifier}" -> "${newIdentifier}"`,
      );

      // Always cleanup old state first (synchronous)
      unsubscribeFromRealtime();
      clear();

      // If new identifier is null, user logged out - cleanup is done
      if (!newIdentifier) {
        console.log('[AssetStore] Logout detected, assets cleared');
        return;
      }

      // If we have a new identifier, fetch assets and subscribe
      try {
        console.log(`[AssetStore] Fetching assets for new enterprise: ${newIdentifier}`);
        await fetchAssets(newIdentifier);
        subscribeToRealtime();
        console.log('[AssetStore] Assets initialized successfully');
      } catch (err) {
        console.error('[AssetStore] Error initializing assets:', err);
      }
    },
  );

  // ============================================================================
  // Return Store Interface
  // ============================================================================

  return {
    // State
    assets,
    loading,
    error,

    // Getters
    allAssets,
    assetsWithCompanions,
    getAssetById,
    onlineAssets,

    // Actions
    fetchAssets,
    setAlias,
    subscribeToRealtime,
    unsubscribeFromRealtime,
    updateAsset,
    removeAsset,
    refreshAssets,
    clear,
  };
});
